import styled from 'styled-components';

import {flexColumn, flexCenterVertically} from '~/shared/theme/FlexLayout';

import {getLocalizationService} from '../services/localisationService';
import {flipOnLTR} from '../theme/utils';
import {Link} from '../router';
import {body14Normal, body14Bold, smallRegular} from '../theme/typography';

import _FormField from './FormField';

const TextLink = styled(Link)<{isGov?: boolean; isCapitalized?: boolean}>`
  cursor: pointer;
  color: ${({theme}) => theme.colors.contentLink};
  ${({isGov}) => (isGov ? body14Bold : smallRegular)}
  line-height: 1em;
  white-space: nowrap;
  ${flipOnLTR`
    margin-left: 3px;
  `}
  ${({isCapitalized}) => isCapitalized && `
    text-transform: capitalize;
  `}
`;

const ErrorText = styled.div.attrs(() => ({
  role: 'alert',
}))`
  ${body14Normal}
  min-height: 20px;
  line-height: 20px;
  margin-top: auto;
  margin-bottom: 12px;
  color: ${({theme}) => theme.colors.contentError};
  ${flipOnLTR`
    text-align: right;
  `}
`;

const CheckBoxArea = styled.div<{isGov?: boolean}>`
  ${flexColumn}
  margin-top: 8px;
`;

const FormField = styled(_FormField).attrs({
  className: 'agree-to-terms-form-field',
})`
  display: flex;
  height: auto;
  flex: 0 1 auto;

  .checkbox-rectangle {
    ${flipOnLTR`
      margin-left: 8px;
    `}
  }
`;

const TermsFormField = styled(FormField)`
  margin-bottom: 16px;
`;

const PromotionsFormField = styled(FormField)`
  margin-bottom: 8px;
`;

const Label = styled.label`
  color: ${({theme}) => theme.colors.secondary};
  ${smallRegular}
  text-align: start;
  ${flipOnLTR`
    margin-left: 3px;
  `}
`;

const LabelWrapper = styled.div`
  flex-wrap: wrap;
  ${flexCenterVertically}
`;

const LinksWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

type AgreeToTermsSectionProps = {
  errorText?: string;
  onTermsOfUseClick: () => void;
  onPrivacyPolicyClick: () => void;
  onDealsPolicyClick?: () => void;
  isUpdateUserDetails?: boolean;
  isGov?: boolean;
};

export default function AgreeToTermsSection({
  errorText,
  onTermsOfUseClick,
  onPrivacyPolicyClick,
  onDealsPolicyClick,
  isUpdateUserDetails = false,
  isGov = false,
}: AgreeToTermsSectionProps) {
  /**
   * wantPromotion - used by signup (nextApi)
   * wantPromotions - used by update user details (bff)
   * isUpdateUserDetails is temp solution until both signup will be moved to the bff.
   */
  const {t} = getLocalizationService();
  return (
    <CheckBoxArea className="checkbox-area" isGov={isGov}>
      <TermsFormField
        {...{
          name: 'agreeToTerms',
          type: 'checkbox',
          shouldShowError: false,
          required: true,
          isGov,
          inputProps: {
            alignItems: 'start',
            label: (
              <LabelWrapper>
                <Label>{t('i_read_understood_and_agreed')}</Label>
                <LinksWrapper>
                  <TextLink onClick={onTermsOfUseClick} isGov={isGov}>
                    {t('terms_of_use')}
                  </TextLink>
                  <Label>{t('and')}</Label>
                  {isGov ? (
                    <TextLink
                      onClick={onDealsPolicyClick}
                      isCapitalized
                      isGov={isGov}
                    >
                      {t('deals_policy')}
                    </TextLink>
                  ) : (
                    <TextLink
                      onClick={onPrivacyPolicyClick}
                      isCapitalized
                      isGov={isGov}
                    >
                      {t('privacy_policy')}
                    </TextLink>
                  )}
                </LinksWrapper>
              </LabelWrapper>
            ),
            isPieCheckboxStyles: true,
            outlined: true,
          },
        }}
      />
      <PromotionsFormField
        {...{
          name: isUpdateUserDetails ? 'wantPromotions' : 'wantPromotion',
          type: 'checkbox',
          inputProps: {label: <Label>{t('send_me_emails_with_deals_coupons_and_special_offers')}</Label>, isPieCheckboxStyles: true, outlined: true},
          isGov,
        }}
      />
      <ErrorText>{errorText}</ErrorText>
    </CheckBoxArea>
  );
}
